<script>
import CertPills from '@components/cert-pills.vue'
import GradeList from '@components/booking/grade-list.vue'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import BookingCardPolicies from '@/components/booking/booking-card-policies.vue'
import BookingListFeedback from '@components/booking/booking-list-feedback.vue'
import { mapGetters } from 'vuex'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import formatDateMixin from '@/mixins/formatDateMixin'
import MenuItem from '@/models/general/menuItem'
import baseMixin from '@/mixins/baseMixin'
import Interaction from '@/models/general/interaction'
import InteractionType from '@/shared/constants/core/InteractionType'
import EmitEvent from '@/models/general/emitEvent'
import { BookingCommentType } from '@/shared/constants/booking/BookingCommentType'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import BookingCard from '@/shared/constants/booking/BookingCard'
import BaseDropdownMenu from '@/components/_base-dropdown-menu.vue'
import BookingStatusLabel from './booking-status-label.vue'
import BookingCardIdLabel from './booking-card-id-label.vue'
import ExtendBookingModal from './extend-booking/extend-booking-modal.vue'

export default {
  name: 'BookingCard',
  components: {
    CertPills,
    BookingCardPolicies,
    GradeList,
    BookingListFeedback,
    CandidateImage,
    BookingCardIdLabel,
    BookingStatusLabel,
    BaseDropdownMenu,
    ExtendBookingModal,
  },
  mixins: [baseMixin, formatDateMixin, featureFlagMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      profileImgSrc: null,
      showExtendBookingModal: false,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
      hasLocationBookingCreatePermission:
        'auth/hasLocationBookingCreatePermission',
    }),
    howLate() {
      if (!this.booking.runningLate) return 0

      const items = this.booking.noteList.filter(
        (item) => item.type === BookingCommentType.LATE
      )
      if (items.length === 0) return 0

      const jsonObj = JSON.parse(items[0].text)
      return jsonObj.LateMins
    },
    hasGrades() {
      return this.booking.grades.length > 0
    },
    hasCandidate() {
      return this.booking.candidate && this.booking.candidate.id > 0
    },
    compRunningLateClass() {
      return !this.booking.runningLate ? '' : 'red--text font-weight-medium'
    },
    compDuration() {
      return `${this.renderTime24(
        this.booking.startDateTimeLocal
      )} - ${this.renderTime24(this.booking.endDateTimeLocal)}`
    },
    compCandidateFullName() {
      if (!this.hasCandidate) return ''
      return `${this.booking.candidate.firstName} ${this.booking.candidate.lastName}`
    },
    compFilledCandidateId() {
      return this.hasCandidate ? this.booking.candidate.id : -1
    },
    compFilledCandidateFirstName() {
      return !this.hasCandidate ? null : this.booking.candidate.firstName
    },
    compFilledCandidateLastName() {
      return !this.hasCandidate ? null : this.booking.candidate.lastName
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    isUnapproved() {
      return this.bookingStatus === BookingCard.labelStatus.Unapproved
    },
    bookingStatus() {
      return BookingCard.bookingStatusMap[this.booking.status]
    },
    isUnfilled() {
      return this.bookingStatus === BookingCard.labelStatus.Open
    },
    buttonConfig() {
      const { warning200, blue200, warning, blue, grey500 } = VuetifyColorClass
      return {
        hoverColor: this.isUnfilled ? warning200 : blue200,
        iconColor: grey500,
        iconHoverColor: this.isUnfilled ? warning : blue,
      }
    },
    hasCreateForLocationPerm() {
      const locationId = this.booking?.location?.id

      return locationId && this.hasLocationBookingCreatePermission(locationId)
    },
    cardOptions() {
      const options = [
        {
          icon: 'mdi-arrow-top-right-bottom-left',
          type: 'action',
          text: this.$t('booking.extend.extend'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'extendBooking',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableExtendBooking,
            this.hasCreateForLocationPerm,
          ],
        },
        {
          icon: 'mdi-close-circle-outline',
          type: 'action',
          text: this.$t('booking.cancelBookingBtn'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'cancelBooking',
              value: this.booking.id,
            }),
          }),
          rules: [this.canCreateBookings],
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
  },
  methods: {
    viewCandidate() {
      if (this.hasCandidate) {
        this.$emit('viewCandidate', this.booking.candidate.id)
      }
    },
    updateProfileImage(data) {
      this.profileImgSrc = data
    },
    showCancelBookingDialog() {
      this.$emit('cancelBooking', this.booking)
    },
    toggleExtendBookingModal() {
      this.showExtendBookingModal = !this.showExtendBookingModal
    },
  },
}
</script>

<template>
  <v-card class="mb-5 booking-card custom-elevation rounded-xl" elevation="0">
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6" lg="5" xl="4" class="d-flex">
          <div>
            <v-lazy>
              <CandidateImage
                :id="compFilledCandidateId"
                :first-name="compFilledCandidateFirstName"
                :last-name="compFilledCandidateLastName"
                rounded
                size="xxxl"
                clickable
                @profileImgSrc="updateProfileImage"
                @click="viewCandidate"
              />
            </v-lazy>
          </div>

          <div class="flex-grow-1">
            <BookingCardIdLabel :booking="booking" />

            <h5 class="font-weight-medium subtitle-1">
              <a
                v-if="hasCandidate"
                title="View Profile"
                @click="viewCandidate"
              >
                {{ compCandidateFullName }}
              </a>
              <span v-else>
                {{ this.$t('booking.unfilledBookingText') }}
              </span>
            </h5>

            <p
              :class="[
                'subtitle-1 font-weight-medium mb-0',
                compRunningLateClass,
              ]"
            >
              {{ compDuration }}
              <span
                v-if="booking.runningLate"
                class="booking-card__running-late"
              >
                +{{ $t('booking.minsLateText', { count: howLate }) }}
              </span>
              <span
                title="Classification"
                class="booking-card__classification caption my-0"
              >
                {{ booking.type }}
              </span>
            </p>

            <div v-if="booking.replacing" class="booking-card__replacing">
              {{ $t('booking.replacingLabel') }}:
              <span class="d-inline-block font-weight-medium">
                {{
                  `${booking.replacing.firstName} ${booking.replacing.lastName}`
                }}
              </span>
            </div>

            <GradeList v-if="hasGrades" small :grades="booking.grades" />

            <div v-if="hasCandidate" class="badges mb-2">
              <CertPills :certifications="booking.certChecks" />
              <BookingCardPolicies :policies="booking.agreements" />
            </div>

            <div v-if="selfServiceFeatureFlags.showBookingStatus">
              <BookingStatusLabel :status="bookingStatus" />
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6" lg="5" xl="6">
          <div class="booking-card__details">
            <div v-if="booking.roomNumber" class="booking-card__room">
              {{ $t('booking.roomLabel') }}:
              <span class="font-weight-medium"> {{ booking.roomNumber }} </span>
            </div>

            <div v-if="booking.notes" class="booking-card__notes">
              {{ $t('booking.notesLabel') }}:
              <span>
                {{ booking.notes }}
              </span>
            </div>
          </div>
        </v-col>

        <v-col class="d-flex align-center justify-end" cols="12" md="12" lg="2">
          <div
            v-if="canCreateBookings"
            class="text-right d-flex flex-row-reverse align-center"
          >
            <BaseDropdownMenu
              v-bind="buttonConfig"
              :menu-items="cardOptions"
              tertiary
              :disabled="isUnapproved"
              @cancelBooking="showCancelBookingDialog"
              @extendBooking="toggleExtendBookingModal"
            />

            <BookingListFeedback
              v-if="hasCandidate && !isUnapproved"
              :booking="booking"
            />
          </div>
        </v-col>
      </v-row>

      <div class="clearfix"> </div>
    </v-card-text>
    <ExtendBookingModal
      v-model="showExtendBookingModal"
      :booking="booking"
      :is-unfilled="isUnfilled"
    />
  </v-card>
</template>
